<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '../store'
import ScreenTitle from '../components/ScreenTitle.vue'
import type { LocationView } from '../store'
import { fix, state as positionState, reason as positionReason, ask, nearest } from '../lib/position'

// the coastline is 87 kB of geometry: only loaded once the map is actually opened
const StationMap = defineAsyncComponent(() => import('../components/StationMap.vue'))

const store = useAppStore()
const router = useRouter()
const query = ref('')

const views: LocationView[] = ['map', 'list', 'nearby']

/**
 * Around what: the boat, when its position is known and wanted, or else the place the
 * reader chose. Planning from the kitchen table is done around Harlingen, not around the
 * kitchen; on the water it is the other way about.
 */
const centre = ref<'fix' | 'place'>(fix.value ? 'fix' : 'place')
const placeHere = computed(() => {
  const p = store.place
  return p && typeof p.lat === 'number' && typeof p.lon === 'number' ? { lat: p.lat, lon: p.lon, name: p.name } : null
})
const here = computed(() => (centre.value === 'fix' ? fix.value : placeHere.value))
function useFix() {
  centre.value = 'fix'
  if (!fix.value) ask()
}

/**
 * What is near, in the old app's proportions: fifteen places, ten shallows and five
 * bridges, nothing beyond forty kilometres. The position is asked for on the button and
 * nowhere else, and the last one is kept so the list still works out of signal.
 */
const around = computed(() => {
  const at = here.value
  if (!at) return null
  return {
    stations: nearest(store.stations, at, 40, 15),
    sublocations: nearest(store.sublocations, at, 40, 15),
    shallows: nearest(store.shallows, at, 40, 10),
    bridges: nearest(store.bridges, at, 40, 5)
  }
})
const fixAge = computed(() => {
  if (!fix.value) return ''
  const minutes = Math.round((Date.now() - fix.value.at) / 60_000)
  if (minutes < 2) return store.t('justNow')
  if (minutes < 90) return store.tv('minutesAgo', String(minutes))
  return new Date(fix.value.at).toLocaleString(store.settings.locale, { dateStyle: 'short', timeStyle: 'short' })
})
const km = (n: number) => `${n < 10 ? n.toFixed(1) : Math.round(n)} km`

/**
 * Places, alphabetically, with the derived ones among them rather than in a list of their
 * own. A reader looking for De Cocksdorp is looking for a place to be, and whether its tide
 * is measured there or worked out from Texel is a fact about the data, not about the place.
 * It is said on the row, and the row sits where the name says it should.
 */
const groups = computed(() => {
  const q = query.value.trim().toLowerCase()
  const stationName = (id: string) => store.stations.find(x => x.id === id)?.name ?? id
  const all = [
    ...store.stations.map(x => ({
      ref: `station:${x.id}`, name: x.name, area: x.area, detail: x.id, derived: false
    })),
    ...store.sublocations.map(x => ({
      ref: `sublocation:${x.id}`, name: x.name, area: x.area,
      detail: `${store.t('derivedFrom')} ${stationName(x.station)}`, derived: true
    }))
  ]
    .filter(x => !q || x.name.toLowerCase().includes(q) || x.detail.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
  return [
    { key: 'NL', title: store.t('netherlands'), items: all.filter(x => x.area === 'NL') },
    { key: 'DE', title: store.t('germany'), items: all.filter(x => x.area === 'DE') },
    // Antwerp, Ostend, the Thames: the catalog gives them no country of their own
    { key: 'other', title: store.t('elsewhere'), items: all.filter(x => x.area !== 'NL' && x.area !== 'DE') }
  ].filter(g => g.items.length)
})

const bridgeGroup = computed(() => {
  const q = query.value.trim().toLowerCase()
  return store.bridges.filter(s => !q || s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
})

const shallowGroup = computed(() => {
  const q = query.value.trim().toLowerCase()
  return store.shallows.filter(s => !q || s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
})

function star(ref: string) {
  store.toggleFavourite(ref)
}

function chooseFavourite(ref: string) {
  store.selectPlace(ref)
  void router.push('/')
}

function choose(id: string) {
  store.selectStation(id)
  void router.push('/')
}

function chooseShallow(id: string) {
  store.selectShallow(id)
  void router.push('/')
}

function chooseSublocation(id: string) {
  store.selectSublocation(id)
  void router.push('/')
}

function chooseBridge(id: string) {
  store.selectBridge(id)
  void router.push('/')
}
</script>

<template>
  <main class="screen">
    <div class="header">
      <ScreenTitle>{{ store.t('locations') }}</ScreenTitle>
      <div class="segment">
        <button v-for="v in views" :key="v" :class="{ active: store.settings.locationView === v }" @click="store.settings.locationView = v">{{ store.t(v) }}</button>
      </div>
    </div>
    <!-- kept above both views; the heading stays in the list so an empty one says how to fill it -->
    <template v-if="store.favourites.length || store.settings.locationView === 'list'">
      <h2>{{ store.t('favourites') }}</h2>
      <ul class="stations">
        <li v-for="f in store.favourites" :key="f.ref">
          <button :class="{ selected: f.ref === store.currentRef }" @click="chooseFavourite(f.ref)">
            <span>{{ f.name }}</span>
            <span class="id">{{ f.detail }}</span>
          </button>
          <button class="star on" :aria-label="store.t('removeFavourite')" @click="star(f.ref)">★</button>
        </li>
      </ul>
      <p v-if="!store.favourites.length" class="small muted">{{ store.t('favouriteHint') }}</p>
    </template>
    <!-- What is within reach of where the boat actually is -->
    <template v-if="store.settings.locationView === 'nearby'">
      <!-- the same list around either of two centres; the chosen place needs no permission from anyone -->
      <div v-if="placeHere" class="segment nearbycentre">
        <button :class="{ active: centre === 'place' }" @click="centre = 'place'">{{ placeHere.name }}</button>
        <button :class="{ active: centre === 'fix' }" @click="useFix()">{{ store.t('myPosition') }}</button>
      </div>
      <div v-if="!here" class="nearbyask">
        <p>{{ store.t('nearbyExplain') }}</p>
        <button class="primary big" :disabled="positionState === 'asking'" @click="useFix()">
          {{ positionState === 'asking' ? store.t('positionAsking') : store.t('useMyPosition') }}
        </button>
        <p v-if="positionState === 'denied'" class="notice warn small">{{ store.t('positionDenied') }}</p>
        <p v-else-if="positionState === 'unavailable'" class="notice warn small">{{ store.t('positionUnavailable') }} <span v-if="positionReason" class="muted">({{ positionReason }})</span></p>
      </div>
      <template v-else-if="around">
        <p><strong>{{ centre === 'fix' ? store.t('nearbyHere') : store.tv('nearbyOf', placeHere?.name ?? '') }}</strong></p>
        <p v-if="centre === 'fix'" class="small muted">
          {{ store.t('positionFrom') }} {{ fixAge }}
          <button class="link" @click="ask()">{{ store.t('positionAgain') }}</button>
        </p>
        <template v-for="group in [
          { key: 'stations', title: store.t('stations'), items: around.stations, kind: 'station' },
          { key: 'sublocations', title: store.t('derivedPlaces'), items: around.sublocations, kind: 'sublocation' },
          { key: 'shallows', title: store.t('shallows'), items: around.shallows, kind: 'shallow' },
          { key: 'bridges', title: store.t('bridges'), items: around.bridges, kind: 'bridge' }
        ]" :key="group.key">
          <template v-if="group.items.length">
            <h2>{{ group.title }}</h2>
            <ul class="stations">
              <li v-for="s in group.items" :key="s.id">
                <button :class="{ selected: `${group.kind}:${s.id}` === store.currentRef }" @click="chooseFavourite(`${group.kind}:${s.id}`)">
                  <span>{{ s.name }}</span>
                  <span class="id num">{{ km(s.km) }}</span>
                </button>
                <button class="star" :class="{ on: store.favourited(`${group.kind}:${s.id}`) }"
                        :aria-label="store.t(store.favourited(`${group.kind}:${s.id}`) ? 'removeFavourite' : 'addFavourite')"
                        @click="star(`${group.kind}:${s.id}`)">{{ store.favourited(`${group.kind}:${s.id}`) ? '★' : '☆' }}</button>
              </li>
            </ul>
          </template>
        </template>
        <p v-if="!around.stations.length && !around.shallows.length && !around.bridges.length" class="notice">
          {{ store.t('noneNearby') }}
        </p>
      </template>
    </template>

    <template v-else-if="store.settings.locationView === 'map' && store.stations.length">
      <StationMap :stations="store.stations" :shallows="store.shallows" :sublocations="store.sublocations" :bridges="store.bridges" @select="choose" @select-shallow="chooseShallow" @select-sublocation="chooseSublocation" @select-bridge="chooseBridge" />
    </template>
    <template v-else>
      <input v-model="query" class="search" type="search" :placeholder="store.t('search')" autocomplete="off">
      <template v-for="g in groups" :key="g.key">
        <h2>{{ g.title }}</h2>
        <ul class="stations">
          <li v-for="p in g.items" :key="p.ref">
            <button :class="{ selected: p.ref === store.currentRef }" @click="chooseFavourite(p.ref)">
              <span>{{ p.name }}</span>
              <span class="id">{{ p.detail }}</span>
            </button>
            <button class="star" :class="{ on: store.favourited(p.ref) }"
                    :aria-label="store.t(store.favourited(p.ref) ? 'removeFavourite' : 'addFavourite')"
                    @click="star(p.ref)">{{ store.favourited(p.ref) ? '★' : '☆' }}</button>
          </li>
        </ul>
      </template>
      <template v-if="shallowGroup.length">
        <h2>{{ store.t('shallows') }}</h2>
        <ul class="stations">
          <li v-for="s in shallowGroup" :key="s.id">
            <button :class="{ selected: s.id === store.settings.shallowId }" @click="chooseShallow(s.id)">
              <span>{{ s.name }}</span>
              <span class="id">{{ (s.depth / 100).toFixed(2) }} m</span>
            </button>
            <button class="star" :class="{ on: store.favourited(`shallow:${s.id}`) }" :aria-label="store.t(store.favourited(`shallow:${s.id}`) ? 'removeFavourite' : 'addFavourite')" @click="star(`shallow:${s.id}`)">{{ store.favourited(`shallow:${s.id}`) ? '★' : '☆' }}</button>
          </li>
        </ul>
      </template>
      <template v-if="bridgeGroup.length">
        <h2>{{ store.t('bridges') }}</h2>
        <ul class="stations">
          <li v-for="s in bridgeGroup" :key="s.id">
            <button :class="{ selected: s.id === store.settings.bridgeId }" @click="chooseBridge(s.id)">
              <span>{{ s.name }}</span>
              <span class="id">{{ (s.height / 100).toFixed(2) }} m</span>
            </button>
            <button class="star" :class="{ on: store.favourited(`bridge:${s.id}`) }" :aria-label="store.t(store.favourited(`bridge:${s.id}`) ? 'removeFavourite' : 'addFavourite')" @click="star(`bridge:${s.id}`)">{{ store.favourited(`bridge:${s.id}`) ? '★' : '☆' }}</button>
          </li>
        </ul>
      </template>
    </template>
    <p v-if="!store.stations.length" class="notice">{{ store.sync.state === 'running' ? store.t('syncing') : store.t('noData') }}</p>
  </main>
</template>
